import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "src/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer transition-colors duration-300",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        primary: cn(
          "bg-gradient-to-br from-primary-600/90 to-secondary-600/90 border-gradient-to-r",
          "text-white shadow-xs border border-white",
          "hover:from-primary/70 hover:to-secondary/70",
          "transition-colors duration-300"
        ),
        outline: cn(
          "border-gradient-to-r border bg-gradient-to-br from-primary/10 to-secondary/10 border-white/40",
          "transition-colors duration-300"
        ),
        "outline-secondary":
          "border border-white text-white hover:bg-white/10 transition-colors",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary-500",
        ghost:
          "hover:bg-accent hover:text-gray-900 hover:border-gray-900 hover:bg-gray-900/10",
        white:
          "bg-white text-primary-600 hover:bg-gray-50 transition-colors",
        link: "text-primary underline-offset-4 hover:underline",
        hover: "group group-hover:text-primary-400 hover:text-primary-400",
        none: "",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 px-2 py-1 has-[>svg]:px-1.5",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        xl: "h-12 rounded-md px-8 has-[>svg]:px-6 text-sm lg:text-base",
        "xl-sm": "h-10 rounded-md px-6 has-[>svg]:px-4 text-sm lg:text-base",
        icon: "size-9",
        none: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
